import type { InferRouterOutputs } from "@orpc/server";
import type { ApiRouter } from "@repo/api/orpc/router";
import { orpc } from "@shared/lib/orpc";
import { z } from "zod";

export type UsageSummary = InferRouterOutputs<ApiRouter>["usage"]["summary"];
export type UsageKind = UsageSummary["byKind"][number]["kind"];
export type UsageDaily = InferRouterOutputs<ApiRouter>["usage"]["daily"];

export const USAGE_RANGES = [30, 90] as const;

export const usageSearchSchema = z.object({
	// A stale or hand-edited link falls back to the default range instead of an error.
	days: z
		.union([z.literal(30), z.literal(90)])
		.optional()
		.catch(undefined),
});

/** The chart's range as whole UTC days, today included (what the API and the export take). */
export function usageRange(days: number, now = new Date()) {
	const to = now.toISOString().slice(0, 10);
	const from = new Date(now.getTime() - (days - 1) * 86_400_000)
		.toISOString()
		.slice(0, 10);
	return { from, to };
}

export const usageSummaryQuery = (organizationId: string) =>
	orpc.usage.summary.queryOptions({ input: { organizationId } });

export const usageDailyQuery = (
	organizationId: string,
	range: { from: string; to: string },
) => orpc.usage.daily.queryOptions({ input: { organizationId, ...range } });

/** What each kind of spend is, in the retailer's words. */
export const KIND_LABEL: Record<UsageKind, string> = {
	TURN: "Agent replies",
	RELEVANCE: "Search checks",
	CLASSIFIER: "Message checks",
	ANALYSIS: "Conversation insights",
	ENRICH: "Catalog enrichment",
	EMBED: "Catalog search index",
	STT: "Voice transcription",
	TTS: "Spoken replies",
	WHATSAPP: "WhatsApp messages",
	EVAL: "Evals",
};

/**
 * What Meta bills the retailer directly (ADR-12): shown as its own "Billed by Meta, estimated"
 * block, never in Hephon's spend, its chart or the cap.
 */
export const BILLED_BY_META: readonly UsageKind[] = ["WHATSAPP"];

/** Hephon's own spend rows: what the chart and the cap count. */
export function hephonSpend<T extends { kind: UsageKind }>(
	rows: readonly T[],
): T[] {
	return rows.filter((row) => !BILLED_BY_META.includes(row.kind));
}

/** Meta's message categories, in the retailer's words. */
export const META_CATEGORY_LABEL: Record<string, string> = {
	marketing: "Marketing",
	utility: "Utility",
	authentication: "Authentication",
	service: "Service",
	marketing_lite: "Marketing lite",
};

/**
 * Nine kinds are too many colors for one chart, so the chart stacks three groups, in the
 * validated categorical order (--chart-2, 3, 4). The table and the by-kind list keep every kind.
 */
export const SPEND_GROUPS = [
	{
		key: "chats",
		label: "Shopper chats",
		color: "var(--color-chart-2)",
		kinds: ["TURN", "RELEVANCE", "CLASSIFIER", "STT", "TTS"],
	},
	{
		key: "catalog",
		label: "Catalog",
		color: "var(--color-chart-3)",
		kinds: ["ENRICH", "EMBED"],
	},
	{
		key: "insights",
		label: "Insights and evals",
		color: "var(--color-chart-4)",
		kinds: ["ANALYSIS", "EVAL"],
	},
] as const satisfies ReadonlyArray<{
	key: string;
	label: string;
	color: string;
	kinds: readonly UsageKind[];
}>;

export type SpendGroup = (typeof SPEND_GROUPS)[number]["key"];

export function groupOf(kind: UsageKind): SpendGroup {
	return (
		SPEND_GROUPS.find((group) =>
			(group.kinds as readonly UsageKind[]).includes(kind),
		)?.key ?? "chats"
	);
}

/** Dollars with cents; under a cent, to the hundredth of a cent, since agent turns cost that. */
export function usd(value: number): string {
	if (value > 0 && value < 0.01) {
		return value < 0.0001 ? "<$0.0001" : `$${value.toFixed(4)}`;
	}
	return value.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
	});
}
